import { AxiosInstance, isAxiosError } from "axios"
import {
  SurveyFillRequest,
  SurveyLanguageRequest,
  SurveyResponse,
  SurveySessionRequest,
  SurveyStartRequest,
  SurveySubmitRequest,
  SurveyTestError,
} from "types/surveyTest.types"
import { errMsgOf } from "utils/errMsgOf"

/** The code of an error body, `{"Err msg": "...", "code": "..."}`, parsed or as JSON text; otherwise undefined. */
function codeOf(body: unknown): string | undefined {
  let value = body
  if (typeof value === "string") {
    try {
      value = JSON.parse(value)
    } catch {
      return undefined
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined
  const code = (value as Record<string, unknown>).code
  return typeof code === "string" && code !== "" ? code : undefined
}

/**
 * One call of the link test on the project's route. An error body, with any status, throws a
 * SurveyTestError with the server's message and code.
 */
async function call(
  axiosInstance: AxiosInstance,
  route: string,
  id: string,
  owner: string,
  body: unknown
): Promise<SurveyResponse> {
  const hint = owner ? `&hint_owner=${encodeURIComponent(owner)}` : ""
  let res
  try {
    res = await axiosInstance.post(`/${route}?id=${encodeURIComponent(id)}${hint}`, body)
  } catch (e) {
    if (isAxiosError(e) && e.response) {
      const msg = errMsgOf(e.response.data)
      if (msg !== null) throw new SurveyTestError(msg, codeOf(e.response.data))
    }
    throw e
  }
  const msg = errMsgOf(res.data)
  if (msg !== null) throw new SurveyTestError(msg, codeOf(res.data))
  return res.data as SurveyResponse
}

/** Starts a test of the project's SAVED questionnaire: the first screen, with the new session. */
export async function surveyTestStart(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  body: SurveyStartRequest
): Promise<SurveyResponse> {
  return call(axiosInstance, "survey-test-start", id, owner, body)
}

/** The session's current screen, to resync after a stale step. */
export async function surveyTestScreen(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  body: SurveySessionRequest
): Promise<SurveyResponse> {
  return call(axiosInstance, "survey-test-screen", id, owner, body)
}

/** Answers the current screen: the next screen, or the same one with its errors or a revealed card. */
export async function surveyTestSubmit(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  body: SurveySubmitRequest
): Promise<SurveyResponse> {
  return call(axiosInstance, "survey-test-submit", id, owner, body)
}

/** Fills the rest at random, after the screen's answers if given: the end or terminate with every answer. */
export async function surveyTestFill(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  body: SurveyFillRequest
): Promise<SurveyResponse> {
  return call(axiosInstance, "survey-test-fill", id, owner, body)
}

/** The current screen in another language. */
export async function surveyTestLanguage(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  body: SurveyLanguageRequest
): Promise<SurveyResponse> {
  return call(axiosInstance, "survey-test-language", id, owner, body)
}

/** Ends the session. */
export async function surveyTestEnd(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  body: SurveySessionRequest
): Promise<void> {
  await call(axiosInstance, "survey-test-end", id, owner, body)
}
